/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { ModifierGroup } from "types/models/menu"
import { isDev, getApiUrl } from "constants/domain"
import { currencyToMinorUnit } from "utils/currency"
import { ResponseWithError } from "types/api"

const NAMESPACE = "MODIFIER_GROUP"

// actions
const CREATE_MODIFIER_GROUP = `${NAMESPACE}/CREATE_MODIFIER_GROUP`
const EDIT_MODIFIER_GROUP = `${NAMESPACE}/EDIT_MODIFIER_GROUP`
const PROOF_MODIFIER_GROUP = `${NAMESPACE}/PROOF_MODIFIER_GROUP`
const DELETE_MODIFIER_GROUP = `${NAMESPACE}/DELETE_MODIFIER_GROUP`
const LINK_ITEM_TO_MODIFIER_GROUP = `${NAMESPACE}/LINK_ITEM_TO_MODIFIER_GROUP`
const UNLINK_ITEM_TO_MODIFIER_GROUP = `${NAMESPACE}/UNLINK_ITEM_TO_MODIFIER_GROUP`
const CHANGE_MODIFIER_AVAILABILITY = `${NAMESPACE}/CHANGE_MODIFIER_AVAILABILITY`

export const actions = {
  CREATE_MODIFIER_GROUP,
  PROOF_MODIFIER_GROUP,
  DELETE_MODIFIER_GROUP,
  LINK_ITEM_TO_MODIFIER_GROUP,
  UNLINK_ITEM_TO_MODIFIER_GROUP,
  EDIT_MODIFIER_GROUP,
  CHANGE_MODIFIER_AVAILABILITY,
}

const apiUrl = getApiUrl()
const mexType = isDev()
  ? { "mex-type": "MEXUSERS" }
  : { "x-grabkit-grab-serviceid": "MEXUSERS" }

// actions creators
export const actionCreator = {
  createModifierGroup: (
    newModifierGroup: ModifierGroup,
    currency: string | undefined,
    merchantID?: string,
    merchantGroupID?: string,
    menuGroupID?: string,
  ) => ({
    type: CREATE_MODIFIER_GROUP,
    payload: {
      request: {
        url: `${apiUrl}/food/merchant/${
          menuGroupID ? "v1/menu-groups" : "v2"
        }/modifier-groups`,
        method: "post",
        headers: {
          merchantID,
          merchantGroupID,
          ...mexType,
        },
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
        data: {
          modifierGroup: {
            selectionRangeMin: newModifierGroup.selectionRangeMin,
            selectionRangeMax: newModifierGroup.selectionRangeMax,
            modifierGroupName: newModifierGroup.modifierGroupName,
            nameTranslation: newModifierGroup.nameTranslation,
            modifiers: newModifierGroup.modifiers?.map(mod => ({
              advancedPricing: mod.advancedPricing,
              priceInMin: currencyToMinorUnit(mod.priceInMin, currency) ?? 0,
              modifierID: "",
              modifierName: mod.modifierName,
              nameTranslation: mod.nameTranslation,
              ...(mod.bcrsInfo ? { bcrsInfo: mod.bcrsInfo } : {}),
            })),
            modifierGroupID: "",
          },
        },
      },
      data: {
        modifierGroupID: "",
      },
    },
  }),
  proofModifierGroup: (
    modifierGroupID: string,
    merchantID?: string,
    merchantGroupID?: string,
    menuGroupID?: string,
    showError?: boolean,
  ) => ({
    type: PROOF_MODIFIER_GROUP,
    showError,
    payload: {
      request: {
        url: `${apiUrl}/food/merchant/${
          menuGroupID ? "v1/menu-groups" : "v2"
        }/modifier-groups/${modifierGroupID}/proof`,
        method: "post",
        headers: {
          merchantID,
          merchantGroupID,
          ...mexType,
        },
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
        data: {
          proofImageURL: "",
        },
      },
    },
  }),
  deleteModifierGroup: (
    modifierGroupID: string,
    merchantID?: string,
    merchantGroupID?: string,
    menuGroupID?: string,
  ) => ({
    type: DELETE_MODIFIER_GROUP,
    showError: true,
    payload: {
      request: {
        url: `${apiUrl}/food/merchant/${
          menuGroupID ? "v1/menu-groups" : "v2"
        }/modifier-groups/${modifierGroupID}`,
        method: "delete",
        headers: {
          merchantID,
          merchantGroupID,
          ...mexType,
        },
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
      },
    },
  }),
  linkItemToModifierGroup: (
    modifierGroupID: string,
    itemID: string,
    merchantID: string,
    merchantGroupID?: string,
    menuGroupID?: string,
    errorCallback?: (resp: ResponseWithError) => any,
  ) => ({
    type: LINK_ITEM_TO_MODIFIER_GROUP,
    showError: Boolean(errorCallback),
    payload: {
      request: {
        url: `${apiUrl}/food/merchant/${
          menuGroupID ? "v1/menu-groups" : "v2"
        }/item-modifier-groups`,
        method: "post",
        headers: {
          merchantID,
          merchantGroupID,
          ...mexType,
        },
        data: {
          itemID,
          modifierGroupID,
          menuGroupID,
        },
      },
    },
    errorCallback,
  }),
  UnLinkItemFromModifierGroup: (
    modifierGroupID: string,
    itemID: string,
    merchantID: string,
    merchantGroupID?: string,
    menuGroupID?: string,
    errorCallback?: (resp: ResponseWithError) => any,
  ) => ({
    type: UNLINK_ITEM_TO_MODIFIER_GROUP,
    showError: true,
    payload: {
      request: {
        url: `${apiUrl}/food/merchant/${
          menuGroupID ? "v1/menu-groups" : "v2"
        }/item-modifier-groups`,
        method: "delete",
        headers: {
          merchantID,
          merchantGroupID,
          ...mexType,
        },
        data: {
          itemID,
          modifierGroupID,
          menuGroupID,
        },
      },
    },
    errorCallback,
  }),
  editModifierGroup: (
    editedModifierGroup: ModifierGroup,
    currency: string | undefined,
    merchantID?: string,
    merchantGroupID?: string,
    menuGroupID?: string,
  ) => ({
    type: EDIT_MODIFIER_GROUP,
    payload: {
      request: {
        url: `${apiUrl}/food/merchant/${
          menuGroupID ? "v1/menu-groups" : "v2"
        }/modifier-groups/${editedModifierGroup.modifierGroupID}`,
        method: "put",
        headers: {
          merchantID,
          merchantGroupID,
          ...mexType,
        },
        params: menuGroupID
          ? {
              menuGroupID,
            }
          : undefined,
        data: {
          modifierGroup: {
            modifierGroupID: editedModifierGroup.modifierGroupID,
            selectionRangeMin: editedModifierGroup.selectionRangeMin,
            selectionRangeMax: editedModifierGroup.selectionRangeMax,
            modifierGroupName: editedModifierGroup.modifierGroupName,
            nameTranslation: editedModifierGroup.nameTranslation,
            modifiers: editedModifierGroup.modifiers?.map(mod => ({
              advancedPricing: mod.advancedPricing,
              priceInMin: currencyToMinorUnit(mod.priceInMin, currency) ?? 0,
              modifierID: mod.modifierID,
              modifierName: mod.modifierName,
              nameTranslation: mod.nameTranslation,
              sortOrder: mod.sortOrder,
              ...(mod.availableStatus !== 0
                ? {
                    availableStatus: mod.availableStatus,
                  }
                : {}),
              ...(mod.bcrsInfo ? { bcrsInfo: mod.bcrsInfo } : {}),
            })),
          },
        },
      },
    },
  }),
  changeModifierAvailability: (
    availabilityStatus: number,
    modifierIDs: string[],
    merchantID: string,
  ) => ({
    type: CHANGE_MODIFIER_AVAILABILITY,
    showError: true,
    payload: {
      request: {
        url: `${apiUrl}/food/merchant/v2/modifiers/available`,
        method: "put",
        headers: {
          merchantID,
          ...mexType,
        },
        data: {
          availableStatus: availabilityStatus,
          modifierIDs,
        },
      },
    },
  }),
}
